// The two hex-grid layers on the WebGL2 engine (R456, R501,
// ui-map-interface.md §4.3).
//
// The shipped map never draws the hex grid: territory and terrain render as
// smoothed organic shapes, and the grid under them is an implementation
// detail. These two layers put it back, on a key, for the owner and for a
// reviewer who has to read exactly which cells a faction holds.
//
// They are the port of `render/hexgrid.ts`, and they keep its two rules:
//
//   - **Static between owner changes.** Nothing here advances on a clock. A
//     rebuild writes the instances once and every later frame re-flushes
//     them, so a still map costs one `flush` a layer.
//   - **The rebuild is LAZY.** Both layers ship hidden, and the engine skips
//     `update` on an invisible layer (`layer.ts`), so a whole season of
//     flips costs one flag each and the reader who presses the key pays the
//     retrace once, on the first visible frame.
//
// The fill is one `hex` sprite per owned cell, tinted with the faction's own
// colour at `HEXGRID.fillAlpha` — this view has its own wash and does NOT
// read the territory tint. The PixiJS layer draws one path of every hex and
// one `fill()` per faction; an instanced sprite is the same idea on this
// engine, and the atlas cell is baked from `world.hexCorner`, so the drawn
// hex IS the map's hex. The border is one segment run per traced loop of
// `traceHexLoops` and nothing else: no Laplacian relax and no Chaikin,
// because the corners are the whole point of the view.

import { HEXGRID, TERRITORY_LIMIT } from '../../gen/presentation';
import { ownedCellsByFaction } from '../../render/hexgrid-cells';
import { traceHexLoops } from '../../render/outlines';
import { dashLoopFit, dashPattern, dashRuns } from '../../render/faction-marks';
import type { FactionEdgeStyle } from '../../render/scene-look';
import { cellCenter, factionColor } from '../../world';
import type { AtlasEntry } from '../gfx/atlas';
import { F_ROUND, type DrawCtx, type FrameInfo, type Layer } from '../layer';
import type { BatchSource, QuadSink, SegmentSink } from './batches';

/** One straight piece of a line: x0, y0, x1, y1. */
export type Piece = [number, number, number, number];

/**
 * The dashes of one closed loop of hex corners, as straight pieces
 * (R3277). The pattern is a faction dash of `factionmarks.toml`, and the
 * loop takes the stretch of the traced outline (`dashLoopFit`, R1577), so
 * the pattern closes on itself. A run that turns a corner is two pieces,
 * one each side of the corner. An empty pattern answers every edge whole.
 */
export function dashedLoop(loop: readonly (readonly [number, number])[], pattern: readonly number[]): Piece[] {
  const n = loop.length;
  const edges: Piece[] = [];
  const starts: number[] = [];
  let total = 0;
  for (let k = 0; k < n; k++) {
    const a = loop[k], b = loop[(k + 1) % n];
    edges.push([a[0], a[1], b[0], b[1]]);
    starts.push(total);
    total += Math.hypot(b[0] - a[0], b[1] - a[1]);
  }
  const { on, period } = dashRuns(pattern);
  if (period <= 0 || total <= 0) return edges;
  const fit = dashLoopFit(total, period);
  const step = period * fit;
  const out: Piece[] = [];
  for (let base = 0; base < total - 1e-6; base += step) {
    for (const [s, e] of on) {
      const s0 = base + s * fit, s1 = Math.min(total, base + e * fit);
      if (s0 >= total) break;
      for (let k = 0; k < n; k++) {
        const [x0, y0, x1, y1] = edges[k];
        const len = Math.hypot(x1 - x0, y1 - y0);
        const lo = Math.max(s0, starts[k]), hi = Math.min(s1, starts[k] + len);
        if (len <= 0 || hi <= lo) continue;
        const t0 = (lo - starts[k]) / len, t1 = (hi - starts[k]) / len;
        out.push([x0 + (x1 - x0) * t0, y0 + (y1 - y0) * t0, x0 + (x1 - x0) * t1, y0 + (y1 - y0) * t1]);
      }
    }
  }
  return out;
}

/** How many hexes one batch starts with. It grows with the claim. */
const FILL_CAPACITY = 4096;

/** How many border segments one batch starts with. */
const BORDER_CAPACITY = 4096;

/**
 * What the two layers share: the owners reference, the blocked mask and the
 * lazy dirty flag.
 *
 * The owners array is HELD and not copied, exactly as `render/hexgrid.ts`
 * holds it: `feed.ts` mutates the territory layer's own bytes in place on
 * every flip, so a layer that copied would draw the world of a season ago.
 */
abstract class HexLayer implements Layer {
  abstract readonly name: string;

  protected owners: Uint8Array | null = null;
  protected blocked: Uint8Array | null = null;
  private shown = false;
  private dirty = false;
  /** How many times the layer rebuilt. The tests read it. */
  rebuilds = 0;

  /**
   * Takes the live owners array and the blocked mask, and stales the
   * picture. The hello calls it; a flip calls `invalidate`.
   */
  setOwners(owners: Uint8Array, blocked: Uint8Array | null): void {
    this.owners = owners;
    this.blocked = blocked;
    this.dirty = true;
  }

  /**
   * Marks the picture stale. One flag, however many flips arrive, and a flip
   * while the layer is hidden costs exactly this write.
   */
  invalidate(): void { this.dirty = true; }

  update(_frame: FrameInfo): void {
    // The engine calls this on a visible layer only, so the hidden state is
    // the whole of the laziness — there is no second guard to keep in step.
    if (!this.dirty) return;
    this.dirty = false;
    this.rebuilds++;
    this.rebuild();
  }

  abstract draw(ctx: DrawCtx): void;

  setVisible(v: boolean): void { this.shown = v; }
  get visible(): boolean { return this.shown; }

  /** Writes the instances for the owners the layer holds. */
  protected abstract rebuild(): void;
}

/** The filled hexes, under the territory outline. */
export class HexFillLayer extends HexLayer {
  readonly name = 'hexfills';

  private readonly quads: QuadSink;
  private readonly cell: AtlasEntry;

  constructor(src: BatchSource) {
    super();
    this.quads = src.newQuads(FILL_CAPACITY);
    this.cell = src.sprite('hex');
  }

  /** How many hexes the last rebuild wrote. The tests read it. */
  get count(): number { return this.quads.count; }

  draw(ctx: DrawCtx): void { this.quads.flush(ctx.cam); }

  protected rebuild(): void {
    const q = this.quads;
    q.begin();
    const owners = this.owners;
    if (!owners) return;
    // Ascending index order per faction, rock dropped: a stale owners byte
    // under a ridge must not draw a hex over the rock (`hexgrid-cells.ts`).
    const cells = ownedCellsByFaction(owners, this.blocked);
    for (const [faction, list] of cells) {
      const color = factionColor(faction);
      for (let i = 0; i < list.length; i++) {
        const [cx, cy] = cellCenter(list[i]);
        q.sprite(cx, cy, 0, this.cell, color, HEXGRID.fillAlpha);
      }
    }
  }
}

/** The hex outlines, above the fills. */
export class HexBorderLayer extends HexLayer {
  readonly name = 'hexborders';

  private readonly segments: SegmentSink;
  /** The owners with the rock zeroed, which the trace reads. The layer holds
   *  the owners array of the territory layer, so it never writes to it. */
  private traced = new Uint8Array(0);

  constructor(src: BatchSource) {
    super();
    this.segments = src.newSegments(BORDER_CAPACITY);
  }

  /** How many segments the last rebuild wrote. The tests read it. */
  get count(): number { return this.segments.count; }

  /**
   * The faction edge look (R3277). Under `limit` the hex border draws the
   * thin line of `[presentation.territory.limit]` with the faction dash of
   * each faction, so the second channel of §2.2 stands in the hex view as
   * well. The hex fill is the tint of the claim, so the band is not drawn
   * here. `dash` and `off` keep the solid hex border.
   */
  setEdge(style: FactionEdgeStyle): void {
    const limit = style === 'limit';
    if (limit === this.limit) return;
    this.limit = limit;
    this.invalidate();
  }

  /** Does the hex border draw the limit edge? */
  private limit = false;

  draw(ctx: DrawCtx): void { this.segments.flush(ctx.cam); }

  protected rebuild(): void {
    const s = this.segments;
    s.begin();
    let owners = this.owners;
    if (!owners) return;
    // Rock is never owned, so a stale owners byte under a ridge must not draw
    // a border with no fill, as the fill view drops it (R3018).
    const blocked = this.blocked;
    if (blocked) {
      if (this.traced.length !== owners.length) this.traced = new Uint8Array(owners.length);
      const t = this.traced;
      for (let i = 0; i < owners.length; i++) t[i] = blocked[i] ? 0 : owners[i];
      owners = t;
    }
    // One faction at a time, so a run carries one colour. The trace returns
    // closed loops, holes included, so an enclave inside a claim draws its
    // own outline the way the PixiJS layer's `poly()` does.
    const seen = new Set<number>();
    for (let i = 0; i < owners.length; i++) {
      const f = owners[i];
      if (f === 0 || seen.has(f)) continue;
      seen.add(f);
      const color = factionColor(f);
      const loops = traceHexLoops(owners, f);
      if (this.limit) {
        const pattern = dashPattern(f);
        for (const loop of loops) {
          for (const [x0, y0, x1, y1] of dashedLoop(loop, pattern)) {
            s.line(x0, y0, x1, y1, TERRITORY_LIMIT.lineWidth, color, TERRITORY_LIMIT.lineAlpha, F_ROUND);
          }
        }
        continue;
      }
      for (const loop of loops) {
        for (let k = 0; k < loop.length; k++) {
          const a = loop[k];
          const b = loop[(k + 1) % loop.length];
          s.line(a[0], a[1], b[0], b[1],
            HEXGRID.borderWidth, color, HEXGRID.borderAlpha);
        }
      }
    }
  }
}
